import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronDown, ArrowLeft, User, MapPin, Lock, Check } from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import PhoneField, { DIAL_CODES } from "../components/PhoneField";
import PasswordField from "../components/PasswordField";
import { Field } from "./Login";
import { signup, getStates, getCities, getCountries } from "../lib/api";

const OTHER = "__OTHER__";
const TOTAL_STEPS = 4;

export default function Signup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    country: "India",
    countryOther: "",
    state: "Punjab",
    stateOther: "",
    citySelect: "",
    cityOther: "",
    phone: "",
    email: "",
    password: "",
    referralCode: (params.get("ref") || "").toUpperCase(),
  });
  const [dial, setDial] = useState(DIAL_CODES[0]);
  const [countries, setCountries] = useState(["India"]);
  const [states, setStates] = useState(["Punjab"]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCountries().then((d) => setCountries(d.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.country !== "India") {
      setStates([]);
      setCities([]);
      setForm((f) => ({ ...f, state: "", stateOther: "", citySelect: "", cityOther: "" }));
      return;
    }
    getStates("India").then((d) => setStates(d.states || [])).catch(() => {});
  }, [form.country]);

  useEffect(() => {
    if (!form.state || form.state === OTHER) {
      setCities([]);
      return;
    }
    getCities(form.state).then((d) => setCities(d.cities || [])).catch(() => setCities([]));
    setForm((f) => ({ ...f, citySelect: "", cityOther: "" }));
  }, [form.state]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const finalCity =
    form.country !== "India"
      ? form.cityOther.trim()
      : form.citySelect === OTHER
        ? form.cityOther.trim()
        : form.citySelect;
  const finalState = form.state === OTHER ? form.stateOther.trim() : form.state;
  const finalCountry = form.country === "Other" ? form.countryOther.trim() : form.country;
  const emailValid = !form.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const stepValid = {
    1: form.name.trim().length >= 2,
    2: !!finalCountry && (finalState || form.country !== "India") && !!finalCity,
    3: form.phone.trim().length >= 6 && emailValid && form.password.length >= 8,
  };

  const handleNext = async () => {
    if (!stepValid[step]) {
      toast.error("Please complete this step");
      return;
    }
    if (step < 3) {
      setStep(step + 1);
      return;
    }
    // step === 3: submit signup and go to /verify-otp
    setLoading(true);
    try {
      const fullPhone = `${dial.code}${form.phone.trim()}`;
      const res = await signup({
        name: form.name.trim(),
        phone: fullPhone,
        dial_code: dial.code,
        password: form.password,
        email: form.email.trim() || undefined,
        country: finalCountry,
        state: finalState || "-",
        city: finalCity,
        referral_code: (form.referralCode || "").trim() || undefined,
      });
      toast.success("Verification code sent", { description: `SMS sent to ${fullPhone}` });
      nav("/verify-otp", { state: { phone: fullPhone, purpose: "signup" } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else nav("/");
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-white flex flex-col">
        <div className="screen flex-1 flex flex-col">
          {/* Top bar: back + logo + login link */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              data-testid="signup-back-btn"
              className="w-9 h-9 rounded-full border border-neutral-200 flex items-center justify-center hover:bg-neutral-50"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <Logo size="sm" />
            <Link
              to="/login"
              data-testid="go-to-login-link"
              className="h-8 px-3 rounded-full border border-neutral-200 flex items-center text-[12px] font-semibold text-neutral-800 hover:bg-neutral-50"
            >
              Login
            </Link>
          </div>

          {/* Step indicator */}
          <StepIndicator current={step} total={TOTAL_STEPS} />

          <div className="mt-4 text-center">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-brand">
              Step {step} of {TOTAL_STEPS}
            </div>
            <h1
              className="mt-1.5 font-display font-bold text-[24px] tracking-tight text-black"
              data-testid="signup-title"
            >
              {step === 1 && "What's your name?"}
              {step === 2 && "Where are you based?"}
              {step === 3 && "Contact & password"}
            </h1>
            <p className="mt-1 text-[12.5px] text-neutral-500">
              {step === 1 && "We'll use this on your certificate."}
              {step === 2 && "Helps us localise your experience."}
              {step === 3 && "Phone required, email optional."}
            </p>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); handleNext(); }}
            className="mt-6 flex-1 flex flex-col"
          >
            {step === 1 && <StepName form={form} set={set} />}
            {step === 2 && (
              <StepAddress
                form={form}
                set={set}
                countries={countries}
                states={states}
                cities={cities}
              />
            )}
            {step === 3 && (
              <StepContact
                form={form}
                set={set}
                dial={dial}
                setDial={setDial}
                emailValid={emailValid}
              />
            )}

            <div className="mt-6 flex justify-center pb-6">
              <button
                type="submit"
                disabled={loading || !stepValid[step]}
                data-testid={step === 3 ? "signup-submit-btn" : "signup-next-btn"}
                className={`btn-brand h-11 flex items-center justify-center text-[14px] min-w-[220px] ${
                  stepValid[step] && !loading ? "btn-brand--pulse" : ""
                }`}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : step < 3 ? (
                  "Continue"
                ) : (
                  "Send OTP"
                )}
              </button>
            </div>
          </form>

          <p className="text-[13px] text-center text-neutral-500">
            Already have an account?{" "}
            <Link to="/login" className="text-brand font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </AppShell>
  );
}

// ------------------ Step indicator ------------------
function StepIndicator({ current, total }) {
  const icons = [User, MapPin, Lock, Check];
  return (
    <div className="mt-6 flex items-center justify-center gap-2" data-testid="signup-stepper">
      {Array.from({ length: total }).map((_, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        const Icon = icons[i] || Check;
        return (
          <React.Fragment key={n}>
            <div
              data-testid={`step-dot-${n}`}
              className={`h-8 w-8 rounded-full flex items-center justify-center border transition-colors ${
                done
                  ? "bg-brand text-white border-brand"
                  : active
                    ? "bg-white text-brand border-brand shadow-[0_0_0_4px_rgba(255,90,0,0.14)]"
                    : "bg-neutral-100 text-neutral-400 border-neutral-200"
              }`}
            >
              {done ? <Check className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
            </div>
            {n < total && (
              <div className={`h-[2px] w-6 rounded-full ${n < current ? "bg-brand" : "bg-neutral-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ------------------ Step 1: Name ------------------
function StepName({ form, set }) {
  return (
    <div className="space-y-4">
      <Field label="Full name">
        <input
          autoFocus
          data-testid="signup-name-input"
          className="input-native"
          placeholder="Enter your full name"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
        />
      </Field>
    </div>
  );
}

// ------------------ Step 2: Address ------------------
function StepAddress({ form, set, countries, states, cities }) {
  return (
    <div className="space-y-4">
      <Field label="Country">
        <SelectNative
          testId="signup-country-select"
          value={form.country}
          onChange={(v) => set("country", v)}
          options={countries}
          placeholder="Select country"
        />
        {form.country === "Other" && (
          <input
            data-testid="signup-country-other-input"
            className="input-native mt-2"
            placeholder="Type your country"
            value={form.countryOther}
            onChange={(e) => set("countryOther", e.target.value)}
          />
        )}
      </Field>

      {form.country === "India" && (
        <>
          <Field label="State">
            <SelectNative
              testId="signup-state-select"
              value={form.state}
              onChange={(v) => set("state", v)}
              options={[...states, OTHER]}
              labelFor={(v) => (v === OTHER ? "Other (type manually)" : v)}
              placeholder="Select state"
            />
            {form.state === OTHER && (
              <input
                data-testid="signup-state-other-input"
                className="input-native mt-2"
                placeholder="Type your state"
                value={form.stateOther}
                onChange={(e) => set("stateOther", e.target.value)}
              />
            )}
          </Field>

          <Field label="City">
            <SelectNative
              testId="signup-city-select"
              value={form.citySelect}
              onChange={(v) => set("citySelect", v)}
              options={[OTHER, ...cities]}
              labelFor={(v) => (v === OTHER ? "Other (type manually)" : v)}
              placeholder="Select your city"
            />
            {form.citySelect === OTHER && (
              <input
                data-testid="signup-city-other-input"
                className="input-native mt-2"
                placeholder="Type your city name"
                value={form.cityOther}
                onChange={(e) => set("cityOther", e.target.value)}
              />
            )}
          </Field>
        </>
      )}

      {form.country !== "India" && (
        <Field label="City">
          <input
            data-testid="signup-city-other-input"
            className="input-native"
            placeholder="Type your city name"
            value={form.cityOther}
            onChange={(e) => set("cityOther", e.target.value)}
          />
        </Field>
      )}
    </div>
  );
}

// ------------------ Step 3: Contact + Password ------------------
function StepContact({ form, set, dial, setDial, emailValid }) {
  return (
    <div className="space-y-4">
      <Field label="Mobile number">
        <PhoneField
          phone={form.phone}
          onPhoneChange={(v) => set("phone", v)}
          dial={dial}
          onDialChange={setDial}
          testIdPhone="signup-phone-input"
          testIdDial="signup-dial-select"
        />
        <p className="mt-1 text-[11px] text-neutral-500">Required — we'll send an OTP here.</p>
      </Field>

      <Field label="Email (optional)">
        <input
          data-testid="signup-email-input"
          type="email"
          className="input-native"
          placeholder="you@example.com"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
        />
        {form.email && !emailValid && (
          <p className="mt-1 text-[11px] text-red-500">Please enter a valid email.</p>
        )}
      </Field>

      <Field label="Password">
        <PasswordField
          testId="signup-password-input"
          value={form.password}
          onChange={(v) => set("password", v)}
          placeholder="Minimum 8 characters"
          autoComplete="new-password"
          minLength={8}
        />
        <p className="mt-1 text-[11px] text-neutral-500">At least 8 characters.</p>
      </Field>

      <Field label="Referral code (optional)">
        <input
          data-testid="signup-referral-input"
          className="input-native uppercase"
          placeholder="e.g. ARJ4X7QK"
          maxLength={12}
          value={form.referralCode || ""}
          onChange={(e) => set("referralCode", e.target.value.toUpperCase())}
        />
        <p className="mt-1 text-[11px] text-brand font-semibold">Enter a referral code and get 10% extra off.</p>
      </Field>
    </div>
  );
}

function SelectNative({ value, onChange, options, placeholder, testId, labelFor }) {
  return (
    <div className="relative">
      <select
        data-testid={testId}
        className="input-native pr-10 appearance-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{labelFor ? labelFor(o) : o}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
    </div>
  );
}
